"use client";

import React from "react";
import { Loader, Check, ChevronDown } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GetAFreeQuote() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="31340" id="scan-for-a-free-quote" className="dark bg-background text-foreground py-12 md:py-24 px-4 md:px-8 relative overflow-hidden">
      <div ref={ref} className={`max-w-3xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        {}
        <h2 className="font-sans text-xl md:text-6xl font-bold text-center mb-8 md:mb-12 uppercase tracking-tighter text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.2)]">get A FREE QUOTE</h2>

        {}
        <div className="border border-white/10 p-5 md:p-12 bg-white/[0.02] backdrop-blur-md relative">
          <FormProvider formId="epoxy_lux_quote_request_form_flake_metallic_solid_coatings" sectionName="scan-for-a-free-quote" defaultValues={{
          name: "",
          email: "",
          phone: "",
          address: "",
          services: "",
          details: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          address: {
            required: true
          },
          services: {
            required: true
          },
          details: {
            required: false
          }
        }} submitText="SUBMIT REQUEST" successMessage="Thank you. Your request has been received. Our team will contact you within 24 hours." errorMessage="An error occurred. Please try again or call us at (562) 726-9052.">
            <QuoteForm />
          </FormProvider>
        </div>

        {}
        <p className="font-sans text-xs text-white/40 text-center mt-8 tracking-[0.3em] uppercase">
          @valleyvibe.coatings on Instagram
        </p>
      </div>
    </section>;
}
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="text-center py-12 space-y-6 animate-in fade-in duration-500">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-none bg-white/10 text-white mb-4">
          <Check className="w-8 h-8" />
        </div>
        <h3 className="text-lg md:text-2xl font-sans font-bold uppercase tracking-wider text-white">Request Received</h3>
        <p className="text-white/60 font-sans text-sm leading-7">{successMessage}</p>
      </div>;
  }
  const inputClasses = "border-0 border-b border-white/20 bg-transparent rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-white text-white placeholder:text-white/20 h-10 md:h-12 transition-all duration-300";
  return <Form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {}
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-white/60 text-[10px] uppercase tracking-[0.2em] font-bold">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="ENTER NAME" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="text-red-500 text-[10px] uppercase" />
            </FormItem>} />

        {}
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-white/60 text-[10px] uppercase tracking-[0.2em] font-bold">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="EMAIL@EXAMPLE.COM" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="text-red-500 text-[10px] uppercase" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-white/60 text-[10px] uppercase tracking-[0.2em] font-bold">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(000) 000-0000" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="text-red-500 text-[10px] uppercase" />
            </FormItem>} />

        {}
        <FormField control={control} name="address" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-white/60 text-[10px] uppercase tracking-[0.2em] font-bold">
                Project Location
              </FormLabel>
              <FormControl>
                <Input placeholder="STREET, CITY, STATE" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="text-red-500 text-[10px] uppercase" />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="services" render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className="text-white/60 text-[10px] uppercase tracking-[0.2em] font-bold">
              Coating System
            </FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className={`${inputClasses} w-full flex justify-between items-center`}>
                  <SelectValue placeholder="SELECT A SERVICE..." />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-black text-white border-white/20 rounded-none">
                <SelectItem value="Flake System">Flake System ($8/ft²)</SelectItem>
                <SelectItem value="Metallic Epoxy">Metallic Epoxy ($12/ft² min)</SelectItem>
                <SelectItem value="Solid Color">Solid Color Coating</SelectItem>
                <SelectItem value="Concrete Polishing">Concrete Polishing</SelectItem>
                <SelectItem value="Commercial Flooring">Commercial Flooring</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage className="text-red-500 text-[10px] uppercase" />
          </FormItem>} />

      {}
      <FormField control={control} name="details" render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className="text-white/60 text-[10px] uppercase tracking-[0.2em] font-bold">
              Project Details
            </FormLabel>
            <FormControl>
              <Textarea placeholder="DESCRIBE YOUR PROJECT (SQUARE FOOTAGE, CONDITION, ETC...)" {...field} className={`${inputClasses} min-h-[100px] resize-none pt-3`} />
            </FormControl>
            <FormMessage className="text-red-500 text-[10px] uppercase" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-none text-red-500 text-xs uppercase tracking-widest">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 md:h-14 bg-white text-black hover:bg-white/90 rounded-none text-sm md:text-lg font-bold tracking-[0.2em] uppercase transition-all duration-300 mt-2 md:mt-4">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            PROCESSING...
          </> : "SUBMIT REQUEST"}
      </Button>
    </Form>;
}